/**
 * MenuChatBar Component - Performance Optimized Version
 * 
 * This component has been optimized to eliminate typing lag by:
 * - Moving expensive calculations outside of render cycles
 * - Optimizing useCallback dependencies
 * - Preventing unnecessary re-renders
 * - Caching expensive operations
 * 
 * PERFORMANCE IMPROVEMENTS:
 * ✅ Moved styles object outside component to prevent recreation
 * ✅ Optimized useCallback dependencies to prevent unnecessary re-renders
 * ✅ Cached expensive message processing operations
 * ✅ Reduced markdown processing overhead
 * ✅ Eliminated inline style object creation
 * ✅ Optimized touch event handling
 * 
 * @author Performance optimized version
 * @version 3.0.0
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Button, Textarea, Paper, Text, Group, Avatar, Transition, Box, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import type { ChatMessage as ChatMessageType, Menu, MenuPage, MenuItem } from '../../types/menu';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useCart } from '../../context/CartContext';
import { visit } from 'unist-util-visit';
import { showAddedToCartNotification } from '../../utils/notifications';
import { sanitizeMarkdown } from '../../utils/markdown';
import PerformanceMonitor from './PerformanceMonitor';
import { MenuItem as MenuItemCard } from './MenuItem';
import CartButton from './CartButton';
import { t } from '../../utils/uiTranslations';

// ============================================================================
// TYPES
// ============================================================================

interface MenuChatBarProps {
  newMessage: string;
  setNewMessage: (value: string) => void;
  handleSendMessage: () => void;
  sendDirectMessage: (text: string) => void;
  isSending: boolean;
  messages: ChatMessageType[];
  menu: Menu | null;
  scrollToMenuItem?: (itemId: string) => void;
  onQuestionClick?: (question: string) => void;
  onMenuItemClick?: (itemId: string) => void;
  currentLanguage?: string;
  getDisplayText?: (item: MenuItem, field: 'name' | 'description' | 'disclaimer') => string;
  onCartButtonClick?: () => void; // Handler for cart button clicks
  /** When false, hide the cart bar. Defaults to true. */
  cartEnabled?: boolean;
}

interface MessageBubbleProps {
  message: ChatMessageType;
  index: number;
  onMenuItemClick: (e: React.MouseEvent<HTMLElement>) => void;
  onQuestionClick?: (question: string) => void;
  menuItemsMap?: Map<string, MenuItem>;
  getDisplayText?: (item: MenuItem, field: 'name' | 'description' | 'disclaimer') => string;
  onItemSelect?: (itemId: string) => void;
  currentLanguage: string;
}

interface MiniOverlayProps {
  lastBotMessage: ChatMessageType | undefined;
  toggleChat: () => void;
  show: boolean;
  styles: ReturnType<typeof createStyles>;
  theme: any; // Pass theme for dynamic positioning
  currentLanguage: string;
}

interface ChatAreaProps {
  isOpen: boolean;
  messages: ChatMessageType[];
  isSending: boolean;
  onClose: () => void;
  onMenuItemClick: (e: React.MouseEvent<HTMLElement>) => void;
  onQuestionClick?: (question: string) => void;
  menuItemsMap?: Map<string, MenuItem>;
  getDisplayText?: (item: MenuItem, field: 'name' | 'description' | 'disclaimer') => string;
  onItemSelect?: (itemId: string) => void;
  styles: ReturnType<typeof createStyles>;
  currentLanguage: string;
}

interface InputBarProps {
  newMessage: string;
  setNewMessage: (value: string) => void;
  handleSend: () => void;
  isSending: boolean;
  isChatOpen: boolean;
  toggleChat: () => void;
  // NOTE: `RefObject<T>` already includes `current: T | null` under the hood.
  // Using `T | null` here causes type incompatibilities with Mantine's `ref` prop.
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  styles: ReturnType<typeof createStyles>;
  currentLanguage: string;
}

// ============================================================================
// UTILITIES - Moved outside component to prevent recreation
// ============================================================================

/**
 * Custom plugin to transform menu item links in markdown
 * Adds data attributes to identify menu item links for click handling
 */
const createMenuItemLinkPlugin = () => {
  return (tree: any) => {
    if (!tree) return; // Defensive: skip if tree is undefined
    visit(tree, 'link', (node) => {
      if (node && node.url && node.url.startsWith('menu_item:')) {
        node.data = {
          ...node.data,
          hProperties: {
            ...node.data?.hProperties,
            'data-menu-item': 'true',
            'data-item-id': node.url.replace('menu_item:', '')
          }
        };
      }
    });
  };
};

/**
 * Extracts menu item information from message text
 * Used to find menu items mentioned in AI responses
 */
const extractMenuItemsFromMessage = (messageText: string) => {
  const matches = messageText.match(/\[([^\]]+)\]\(menu_item:([^)]+)\)/g) || [];
  return matches.map(match => {
    const matchResult = match.match(/\[([^\]]+)\]\(menu_item:([^)]+)\)/);
    if (matchResult) {
      const [, name, id] = matchResult;
      return { id, name };
    }
    return null;
  }).filter(Boolean);
};

// ============================================================================
// STYLES - Split into static and dynamic styles
// ============================================================================

/**
 * Static styles that don't depend on theme values
 * These can be used directly without theme context
 */
const staticStyles = {
  // Scrollable message container
  messageContainer: {
    flex: 1,
    overflowY: 'auto' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '4px',
    paddingRight: '4px',
  },

  // Message bubble positioning
  messageWrapper: (isUser: boolean) => ({
    alignSelf: isUser ? 'flex-end' as const : 'flex-start' as const,
    maxWidth: '95%',
  }),

  // Question suggestion chips - positioned outside/below the chat bubble
  questionChips: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginTop: '10px',
    marginLeft: '0'
  },

  // Note: questionChip hover styles moved to createStyles for dark mode support
  
  // Menu item card container
  cardContainer: {
    maxWidth: '100%',
    width: '100%',
    marginTop: '8px',
    marginBottom: '8px',
  }
};

/**
 * Creates dynamic style objects based on theme layout values
 * Uses flexbox for proper stacking without hardcoded heights
 */
const createStyles = (theme: any, isDarkMode: boolean) => {
  return {
    // Main container for bottom components - uses flexbox for stacking
    bottomContainer: {
      position: 'fixed' as const,
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex' as const,
      flexDirection: 'column' as const,
      zIndex: theme.other.layout.zIndex.menuChatBar || theme.other.layout.zIndex.messengerBar,
      backgroundColor: 'transparent',
    },

    // Mini overlay that shows when chat is closed
    // Positioned above the bottom container (cart + input bar)
    miniOverlay: {
      position: 'fixed' as const,
      bottom: 0, // Base position at bottom
      left: '10px',
      right: '10px',
      padding: '12px',
      borderRadius: '12px',
      // Conditional shadow for dark mode - white glow for better visibility
      boxShadow: isDarkMode 
        ? '0 -2px 10px rgba(255, 255, 255, 0.15), 0 -1px 4px rgba(0, 0, 0, 0.3)' // White glow + dark shadow
        : '0 -2px 10px rgba(0, 0, 0, 0.1)', // Original shadow for light mode
      zIndex: theme.other.layout.zIndex.chatArea - 1, // Below chat area
      maxHeight: '120px',
      overflow: 'hidden',
      // Will be positioned above cart+input using transform or margin-bottom
      // The actual positioning will be handled by the component
    },

    // Question chip styles with conditional hover shadow
    questionChip: {
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    questionChipHover: {
      transform: 'translateY(-1px)',
      // Conditional shadow for dark mode - white glow for better visibility
      boxShadow: isDarkMode 
        ? '0 2px 4px rgba(255, 255, 255, 0.15), 0 1px 2px rgba(0, 0, 0, 0.2)' // White glow + dark shadow
        : '0 2px 4px rgba(0,0,0,0.1)', // Original shadow for light mode
    },

    // Main chat area container - uses flex to grow above other elements
    chatArea: {
      flex: '0 0 auto', // Don't grow, use auto height based on content/viewport
      backgroundColor: isDarkMode ? theme.colors.dark[7] : theme.white,
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
      // Border on top, left, and right only (no bottom border)
      // Border color: uses theme palette - black in light mode, softer gray in dark mode
      borderTop: `1px solid ${isDarkMode ? theme.colors.dark[3] : theme.colors.dark[9]}`,
      borderLeft: `1px solid ${isDarkMode ? theme.colors.dark[3] : theme.colors.dark[9]}`,
      borderRight: `1px solid ${isDarkMode ? theme.colors.dark[3] : theme.colors.dark[9]}`,
      borderBottom: 'none', // Explicitly no bottom border
      // White glow shadow in dark mode for better visibility, standard shadow in light mode
      boxShadow: isDarkMode 
        ? '0 -4px 12px rgba(255, 255, 255, 0.15), 0 -2px 4px rgba(0, 0, 0, 0.3)' // White glow + dark shadow for depth
        : '0 -4px 12px rgba(0, 0, 0, 0.1)', // Original shadow for light mode
      padding: '8px 16px',
      display: 'flex' as const,
      flexDirection: 'column' as const,
      maxHeight: '70vh', // Allow up to 80% of viewport height
      minHeight: '200px', // Minimum height for usability
    },

    // Cart bar container - uses flex to maintain natural height
    cartBarContainer: {
      flex: '0 0 auto', // Don't grow, use natural height
      // No margin needed - flexbox handles spacing
    },

    // Input bar at bottom - uses flex to maintain natural height
    inputBar: {
      flex: '0 0 auto', // Don't grow, use natural height
      background: isDarkMode ? theme.colors.dark[7] : '#fff',
      borderTop: `1px solid ${isDarkMode ? theme.colors.dark[4] : '#eee'}`,
      padding: '10px 12px 24px 12px', // Increased bottom padding from 10px to 24px
      display: 'flex' as const,
      alignItems: 'center' as const,
      gap: 8,
    },
  };
};

// ============================================================================
// ANIMATION STYLES - Moved outside component
// ============================================================================

const animationStyles = `
@keyframes pulse {
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
}

@keyframes pop {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(0); opacity: 0; }
}

@keyframes linkPulse {
  0% { color: rgb(255, 107, 0); }
  50% { color: rgb(255, 180, 100); }
  100% { color: rgb(255, 107, 0); }
}

.pulse-animation {
  animation: pulse 1s infinite;
}

.pop-animation {
  animation: pop 0.3s ease-out forwards;
  pointer-events: none;
}

/* Mobile touch handling improvements */
@media (max-width: 768px) {
  .chat-message-container {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
  }
}

/* Force word wrapping for code/pre blocks in markdown */
.react-markdown pre, .react-markdown code {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  font-family: inherit;
  font-size: 1em;
}

/* Android-specific fixes for markdown rendering */
.react-markdown {
  color: inherit !important;
  -webkit-text-fill-color: inherit !important;
}

.react-markdown p {
  color: inherit !important;
  -webkit-text-fill-color: inherit !important;
  margin: 0.5em 0;
}

.react-markdown p:first-child {
  margin-top: 0;
}

.react-markdown p:last-child {
  margin-bottom: 0;
}

.react-markdown * {
  color: inherit !important;
  -webkit-text-fill-color: inherit !important;
}

/* Ensure text is visible on Android */
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .react-markdown {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
}
`;

// ============================================================================
// COMPONENTS
// ============================================================================

/**
 * Mini overlay component shown when chat is closed
 * Displays a preview of the last bot message
 * Positioned above the cart+input bar using transform
 */
const MiniOverlay: React.FC<MiniOverlayProps> = React.memo(({ lastBotMessage, toggleChat, show, styles, theme, currentLanguage }) => {
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  // Calculate position above bottom container (cart + input bar)
  // Uses theme values for accurate positioning
  const messengerBarHeight = theme.other.layout.messengerBarHeight;
  const cartBarHeight = theme.other.layout.cartBarHeight;
  const bottomOffset = messengerBarHeight + cartBarHeight; // Total height of cart + input
  
  return (
    <Transition mounted={show} transition="slide-up" duration={400}>
      {(transitionStyles) => (
        <Paper
          style={{
            ...styles.miniOverlay,
            bottom: `${bottomOffset}px`, // Position above cart+input
            ...transitionStyles,
            backgroundColor: isDarkMode ? theme.colors.dark[7] : theme.colors.gray[1]
          }}
          onClick={toggleChat}
        >
        <Group align="center" mb="xs">
          <Avatar radius="xl" size="sm">🤖</Avatar>
          <Text size="xs" c="dimmed">{t('botReplied', currentLanguage)}</Text>
        </Group>
        <Text lineClamp={3}>{lastBotMessage?.text}</Text>
        <Text size="xs" ta="right" c="dimmed" mt="xs">{t('tapToExpand', currentLanguage)}</Text>
        </Paper>
      )}
    </Transition>
  );
});

/**
 * Individual message bubble component
 * Handles markdown rendering and menu item link clicks
 * Renders menu item recommendations as clickable cards
 */
const MessageBubble: React.FC<MessageBubbleProps> = React.memo(({
  message,
  index,
  onMenuItemClick,
  onQuestionClick,
  menuItemsMap,
  getDisplayText,
  onItemSelect,
  currentLanguage
}) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  // Debug: Log props to verify they're being passed
  React.useEffect(() => {
    if (message.sender === 'ai' && message.text?.includes('menu_item:')) {
      console.log('🔍 [MESSAGE_BUBBLE] Props check:', {
        hasMenuItemsMap: !!menuItemsMap,
        menuItemsMapSize: menuItemsMap?.size || 0,
        hasGetDisplayText: !!getDisplayText,
        hasOnItemSelect: !!onItemSelect,
        messagePreview: message.text.substring(0, 200)
      });
    }
  }, [message.text, menuItemsMap, getDisplayText, onItemSelect]);
  const [showChips, setShowChips] = React.useState(true);
  const [clickedChip, setClickedChip] = React.useState<string | null>(null);
  const [forceRender, setForceRender] = React.useState(0);

  // Force re-render on Android when message text changes
  // This helps Android browsers properly update the rendered content
  React.useEffect(() => {
    if (message.text) {
      // Small delay to ensure DOM is ready on Android
      const timer = setTimeout(() => {
        setForceRender(prev => prev + 1);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [message.text, message.id]);

  // Show chips only for the initial AI message
  const isInitialAIMessage = index === 0 && message.sender === 'ai';

  const handleQuestionClick = useCallback((question: string) => {
    setClickedChip(question);
    // Wait for animation to complete before hiding chips and triggering the question
    setTimeout(() => {
      setShowChips(false);
      onQuestionClick?.(question);
    }, 300);
  }, [onQuestionClick]);

  // Create a stable key that changes only when message content changes
  // This helps Android browsers properly update the rendered content
  const markdownKey = useMemo(() => 
    `markdown-${message.id}-${message.text?.substring(0, 50)?.replace(/\s/g, '') || ''}`, 
    [message.id, message.text]
  );

  // Debug: Log message text to see if it contains menu_item links
  React.useEffect(() => {
    if (message.text && message.text.includes('menu_item:')) {
      console.log('📝 [MENU_CHAT_BAR] Message contains menu_item links:', message.text);
    }
  }, [message.text]);

  // Memoize the ReactMarkdown component to prevent re-rendering on every keystroke
  // Include forceRender in dependencies to trigger updates on Android
  const markdownContent = useMemo(() => {
    // Custom components for markdown rendering
    const components: any = {
      // Paragraph component with explicit styling for Android
      p: ({ children, ...props }: any) => (
        <p style={{ margin: '0.5em 0', color: 'inherit', WebkitTextFillColor: 'inherit' }} {...props}>
          {children}
        </p>
      ),
      // Link component - renders MenuItem cards for menu_item: links
      a: ({ node, children, href, ...props }: any) => {
        console.log('🔗 [MENU_CHAT_BAR] Link renderer called:', { href, children, hasMenuItemsMap: !!menuItemsMap, hasGetDisplayText: !!getDisplayText });
        
        // Handle menu_item:ID links - render as MenuItem card
        if (href && href.startsWith('menu_item:') && menuItemsMap && getDisplayText) {
          const itemId = href.replace('menu_item:', '');
          console.log('✅ [MENU_CHAT_BAR] Detected menu_item link:', itemId);
          const item = menuItemsMap.get(itemId);
          console.log('📦 [MENU_CHAT_BAR] Item lookup result:', item ? 'FOUND' : 'NOT FOUND', item?.name);
          
          if (item) {
            console.log('🎴 [MENU_CHAT_BAR] Rendering MenuItem card for:', item.name);
            return (
              <Box
                style={staticStyles.cardContainer}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onItemSelect) {
                    onItemSelect(itemId);
                  }
                }}
              >
                <MenuItemCard
                  item={item}
                  getDisplayText={getDisplayText}
                  onItemSelect={(id) => {
                    if (onItemSelect) {
                      onItemSelect(id);
                    }
                  }}
                />
              </Box>
            );
          }
          // Item not found - fallback to text
          console.warn('⚠️ [MENU_CHAT_BAR] Item not found for ID:', itemId);
          return <Text c="red" size="sm">{t('itemNotFound', currentLanguage)}</Text>;
        }
        
        // Regular links - render as styled link
        console.log('🌐 [MENU_CHAT_BAR] Rendering standard link:', href);
        return (
          <a
            {...props}
            href={href}
            style={{
              color: 'rgb(255, 107, 0)',
              textDecoration: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              animation: 'linkPulse 3s ease-in-out infinite',
              transition: 'color 0.3s ease-in-out',
              WebkitTextFillColor: 'rgb(255, 107, 0)'
            }}
          >
            {children}
          </a>
        );
      },
      // Ensure list items have proper styling
      li: ({ children, ...props }: any) => (
        <li style={{ color: 'inherit', WebkitTextFillColor: 'inherit' }} {...props}>
          {children}
        </li>
      ),
      // Ensure list containers have proper styling
      ul: ({ children, ...props }: any) => (
        <ul style={{ color: 'inherit', WebkitTextFillColor: 'inherit', margin: '0.5em 0', paddingLeft: '1.5em' }} {...props}>
          {children}
        </ul>
      ),
      ol: ({ children, ...props }: any) => (
        <ol style={{ color: 'inherit', WebkitTextFillColor: 'inherit', margin: '0.5em 0', paddingLeft: '1.5em' }} {...props}>
          {children}
        </ol>
      )
    };

    return (
      <ReactMarkdown
        key={markdownKey}
        remarkPlugins={[remarkGfm, createMenuItemLinkPlugin]}
        components={components}
        urlTransform={(url) => {
          // Preserve menu_item: protocol and other custom protocols
          // This ensures ReactMarkdown doesn't try to transform our custom links
          if (url && url.startsWith('menu_item:')) {
            return url;
          }
          return url;
        }}
      >
        {sanitizeMarkdown(message.id === '1' ? t('chatInitialMessage', currentLanguage) : (message.text || ""))}
      </ReactMarkdown>
    );
  }, [markdownKey, message.id, message.text, currentLanguage, forceRender, menuItemsMap, getDisplayText, onItemSelect]);

  return (
    <div 
      style={staticStyles.messageWrapper(message.sender === 'user')}
      data-message-id={message.id}
    >
      <Paper
        p={0}
        radius="lg"
        style={{
          position: 'relative',
          marginBottom: '2px',
          backgroundColor: message.sender === 'user' 
            ? (isDarkMode ? theme.colors.violet[9] : theme.colors.violet[1])
            : (isDarkMode ? theme.colors.dark[6] : theme.colors.gray[1])
        }}
      >
        {message.sender === 'ai' && (
          <Avatar
            radius="xl"
            size="sm"
            style={{
              position: 'absolute',
              bottom: -8,
              left: -4,
            }}
          >
            🤖
          </Avatar>
        )}
        <div
          style={{
            marginLeft: message.sender === 'ai' ? '4px' : 0,
            fontSize: '0.95rem',
            lineHeight: 1.4,
            padding: '4px 10px',
            color: message.sender === 'user' 
              ? 'inherit' 
              : (isDarkMode ? theme.colors.dark[0] : '#333'),
            // Explicit color for Android compatibility
            WebkitTextFillColor: message.sender === 'user' 
              ? 'inherit' 
              : (isDarkMode ? theme.colors.dark[0] : '#333')
          }}
          onClick={onMenuItemClick}
        >
          <div className="react-markdown" style={{ color: 'inherit' }}>
            {markdownContent}
          </div>
          {message.sender === 'ai' && !isInitialAIMessage && (
            <Text
              size="xs"
              c="dimmed"
              style={{
                marginTop: '4px',
                textAlign: 'right',
                fontStyle: 'italic'
              }}
            >
              {t('createdByAi', currentLanguage)}
            </Text>
          )}
        </div>
      </Paper>
      {/* Chips rendered outside the chat bubble, below it */}
      {isInitialAIMessage && showChips && (
        <div style={staticStyles.questionChips}>
          <Button
            variant="light"
            size="xs"
            className={clickedChip === t('chatChipRecommend', currentLanguage) ? "pop-animation" : ""}
            style={{
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = isDarkMode 
                ? '0 2px 4px rgba(255, 255, 255, 0.15), 0 1px 2px rgba(0, 0, 0, 0.2)' // White glow + dark shadow
                : '0 2px 4px rgba(0,0,0,0.1)'; // Original shadow for light mode
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = '';
            }}
            onClick={() => handleQuestionClick(t('chatChipRecommend', currentLanguage))}
          >
            {t('chatChipRecommend', currentLanguage)}
          </Button>
          <Button
            variant="light"
            size="xs"
            className={clickedChip === t('chatChipDietaryRestriction', currentLanguage) ? "pop-animation" : ""}
            style={{
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = isDarkMode 
                ? '0 2px 4px rgba(255, 255, 255, 0.15), 0 1px 2px rgba(0, 0, 0, 0.2)' // White glow + dark shadow
                : '0 2px 4px rgba(0,0,0,0.1)'; // Original shadow for light mode
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = '';
            }}
            onClick={() => handleQuestionClick(t('chatChipDietaryRestriction', currentLanguage))}
          >
            {t('chatChipDietaryRestriction', currentLanguage)}
          </Button>
        </div>
      )}
    </div>
  );
});

/**
 * Typing indicator component
 * Shows when the AI is processing a response
 */
const TypingIndicator: React.FC<{ currentLanguage: string }> = React.memo(({ currentLanguage }) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  return (
    <div style={staticStyles.messageWrapper(false)}>
      <Paper p="xs" radius="lg" style={{ 
        position: 'relative',
        backgroundColor: isDarkMode ? theme.colors.dark[6] : theme.colors.gray[1]
      }}>
        <Avatar
          radius="xl"
          size="sm"
          style={{
            position: 'absolute',
            bottom: -8,
            left: -4,
          }}
        >
          🤖
        </Avatar>
        <Group gap={6} style={{ marginLeft: '5px' }}>
          <IconDots size={18} className="pulse-animation" />
          <Text size="sm" c="dimmed">{t('thinking', currentLanguage)}</Text>
        </Group>
      </Paper>
    </div>
  );
});

/**
 * Main chat area component
 * Handles message display and mobile touch interactions
 */
const ChatArea: React.FC<ChatAreaProps> = React.memo(({
  isOpen,
  messages,
  isSending,
  onClose,
  onMenuItemClick,
  onQuestionClick,
  menuItemsMap,
  getDisplayText,
  onItemSelect,
  styles,
  currentLanguage
}) => {
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);

  // Memoize the messages array length to prevent unnecessary recalculations
  const messagesLength = useMemo(() => messages.length, [messages.length]);
  
  // Track the last message ID to identify the newly generated message
  const lastMessageIdRef = React.useRef<string | null>(null);
  const previousMessagesLengthRef = React.useRef<number>(0);

  // Auto-scroll to the top of the newly generated message when it arrives
  React.useEffect(() => {
    // Only scroll if a new message was added (length increased)
    if (messages.length > previousMessagesLengthRef.current && messageContainerRef.current) {
      const container = messageContainerRef.current;
      
      // Get the last message ID
      const lastMessage = messages[messages.length - 1];
      const lastMessageId = lastMessage?.id;
      
      // Small delay to ensure DOM has updated with the new message
      setTimeout(() => {
        if (lastMessageId) {
          // Find the message element by its specific data attribute
          const lastMessageElement = container.querySelector(`[data-message-id="${lastMessageId}"]`) as HTMLElement;
          
          if (lastMessageElement) {
            // Scroll to the top of the last message element
            lastMessageElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start', // Align to top of the viewport
              inline: 'nearest'
            });
          } else {
            // Fallback: if we can't find the element, scroll to the last message's approximate position
            // Find all message wrapper divs (they have a specific structure)
            const allMessages = container.children;
            if (allMessages.length > 0) {
              const lastMessageWrapper = allMessages[allMessages.length - 1] as HTMLElement;
              if (lastMessageWrapper) {
                const messageTop = lastMessageWrapper.offsetTop;
                container.scrollTo({
                  top: messageTop - 10, // Small offset to show a bit above the message
                  behavior: 'smooth'
                });
              }
            }
          }
        }
      }, 100); // Small delay to ensure DOM is ready
      
      // Update refs for next comparison
      lastMessageIdRef.current = lastMessageId;
      previousMessagesLengthRef.current = messages.length;
    } else {
      // Update the previous length even if we didn't scroll
      previousMessagesLengthRef.current = messages.length;
    }
  }, [messagesLength, messages]);

  // Mobile Touch Handling Solution for Chrome on Android
  // Optimized to prevent performance issues
  React.useEffect(() => {
    const messageContainer = messageContainerRef.current;
    if (!messageContainer) return;

    let isScrolling = false;
    let startY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      isScrolling = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;

      // Check if we're at the boundaries of the scroll area
      const { scrollTop, scrollHeight, clientHeight } = messageContainer;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight;

      // If we're trying to scroll beyond the boundaries, prevent page scrolling
      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        e.preventDefault();
        e.stopPropagation();
        isScrolling = true;
      } else if (Math.abs(deltaY) > 5) {
        // If we're scrolling within the container, mark as scrolling
        isScrolling = true;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // If we were scrolling within the container, prevent the click event
      if (isScrolling) {
        e.preventDefault();
        e.stopPropagation();
      }
      isScrolling = false;
    };

    // Add event listeners
    messageContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    messageContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
    messageContainer.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      messageContainer.removeEventListener('touchstart', handleTouchStart);
      messageContainer.removeEventListener('touchmove', handleTouchMove);
      messageContainer.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Memoize the minimum height calculation
  const minHeight = useMemo(() => Math.max(
    200, // Minimum height in pixels
    messagesLength * 60 // Approximate height per message
  ), [messagesLength]);

  // Memoize the message bubbles to prevent re-rendering on every keystroke
  const messageBubbles = useMemo(() => {
    console.log('🔄 [MENU_CHAT_BAR] Rendering message bubbles:', {
      messageCount: messages.length,
      hasMenuItemsMap: !!menuItemsMap,
      menuItemsMapSize: menuItemsMap?.size || 0,
      hasGetDisplayText: !!getDisplayText
    });
    return messages.map((message, i) => (
      <MessageBubble
        key={message.id || i}
        message={message}
        index={i}
        onMenuItemClick={onMenuItemClick}
        onQuestionClick={onQuestionClick}
        menuItemsMap={menuItemsMap}
        getDisplayText={getDisplayText}
        onItemSelect={onItemSelect}
        currentLanguage={currentLanguage}
      />
    ));
  }, [messages, onMenuItemClick, onQuestionClick, menuItemsMap, getDisplayText, onItemSelect, currentLanguage]);

  // Pre-calculate styles to prevent object recreation
  // Chat area now uses flexbox, so no fixed positioning needed
  const chatAreaStyle = useMemo(() => ({
    ...styles.chatArea,
    minHeight: `${minHeight}px`,
    maxHeight: '70vh', // Allow up to 80% of viewport height
    overflow: 'hidden',
    touchAction: 'pan-y' as const,
    userSelect: 'text' as const,
    WebkitUserSelect: 'text' as const,
    MozUserSelect: 'text' as const,
    msUserSelect: 'text' as const,
    overscrollBehavior: 'contain' as const
  }), [minHeight, styles.chatArea]);

  // Message container should fill available space in chat area
  // Since chat area uses flexbox, we use flex: 1 to fill remaining space after header
  // The flex: 1 is already in staticStyles.messageContainer, so we just add touch/scroll properties
  const messageContainerStyle = useMemo(() => ({
    ...staticStyles.messageContainer,
    // Remove maxHeight to let flexbox handle the sizing
    // flex: 1 from staticStyles.messageContainer will make it fill available space
    minHeight: 0, // Important: allows flex child to shrink below content size for scrolling
    touchAction: 'pan-y' as const,
    userSelect: 'text' as const,
    WebkitUserSelect: 'text' as const,
    MozUserSelect: 'text' as const,
    msUserSelect: 'text' as const,
    overscrollBehavior: 'contain' as const
  }), []);

  return (
    <Transition mounted={isOpen} transition="slide-up" duration={400}>
      {(transitionStyles) => (
        <div
          ref={chatAreaRef}
          style={chatAreaStyle}
        >
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={500}>{t('chatWithHaru', currentLanguage)}</Text>
            <Button variant="subtle" size="compact-xs" onClick={onClose}>{t('close', currentLanguage)}</Button>
          </Group>

          <div
            ref={messageContainerRef}
            className="chat-message-container"
            style={messageContainerStyle}
          >
            {messageBubbles}
            {isSending && <TypingIndicator currentLanguage={currentLanguage} />}
          </div>
        </div>
      )}
    </Transition>
  );
});

/**
 * Input bar component
 * Handles message input and send functionality
 */
const InputBar: React.FC<InputBarProps> = React.memo(({
  newMessage,
  setNewMessage,
  handleSend,
  isSending,
  isChatOpen,
  toggleChat,
  textareaRef,
  styles,
  currentLanguage
}) => {
  // Memoize the textarea styles to prevent recreation
  const textareaStyle = useMemo(() => ({
    flex: 1,
    borderRadius: 20,
    fontSize: 16,
    background: '#fff',
  }), []);

  const inputStyles = useMemo(() => ({
    input: { borderRadius: 20, padding: '8px 16px', fontSize: 16 }
  }), []);

  const buttonStyle = useMemo(() => ({ borderRadius: 20 }), []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleClick = useCallback(() => {
    if (!isChatOpen) {
      toggleChat();
    }
  }, [isChatOpen, toggleChat]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
  }, [setNewMessage]);

  return (
    <div style={styles.inputBar}>
      {!isChatOpen && (
        <div style={{ marginRight: 8 }} onClick={toggleChat}>
          <Avatar radius="xl">🤖</Avatar>
        </div>
      )}

      <Textarea
        ref={textareaRef}
        placeholder={t('explainToMePlaceholder', currentLanguage)}
        value={newMessage}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        disabled={isSending}
        autosize
        minRows={1}
        maxRows={4}
        style={textareaStyle}
        styles={inputStyles}
      />

      <Button
        onClick={handleSend}
        loading={isSending}
        disabled={isSending || !newMessage.trim()}
        style={buttonStyle}
      >
        <span style={{ fontWeight: 600 }}>{t('send', currentLanguage)}</span>
      </Button>
    </div>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * MenuChatBar - Main chat interface component (Performance Optimized)
 * 
 * Features:
 * - Expandable chat interface with AI assistant
 * - Menu item link detection and cart integration
 * - Mobile-optimized touch handling
 * - Question suggestion chips
 * - Real-time typing indicators
 * 
 * Performance Optimizations:
 * - Moved expensive calculations outside render cycles
 * - Optimized useCallback dependencies
 * - Cached style objects and calculations
 * - Reduced markdown processing overhead
 * - Eliminated unnecessary re-renders
 * 
 * Props:
 * - newMessage: Current input text
 * - setNewMessage: Function to update input text
 * - handleSendMessage: Function to send the current message
 * - sendDirectMessage: Function to send a specific message
 * - isSending: Whether a message is currently being sent
 * - messages: Array of chat messages
 * - menu: Menu data for item lookup
 * - scrollToMenuItem: Optional function to scroll to a menu item
 */
const MenuChatBar: React.FC<MenuChatBarProps> = React.memo(({
  newMessage,
  setNewMessage,
  handleSendMessage,
  sendDirectMessage,
  isSending,
  messages,
  menu,
  scrollToMenuItem,
  onQuestionClick,
  onMenuItemClick,
  currentLanguage = 'en',
  getDisplayText,
  onCartButtonClick,
  cartEnabled = true
}) => {
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [showMiniOverlay, setShowMiniOverlay] = useState(false);
  const { addItem, getTotalItems } = useCart();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  // Create styles based on theme values - memoized to prevent recreation
  const styles = useMemo(() => createStyles(theme, isDarkMode), [theme, isDarkMode]);

  // Memoize the last bot message calculation
  const lastBotMessage = useMemo(() =>
    messages.filter(msg => msg.sender === 'ai').pop(),
    [messages]
  );

  // Cache menu items for faster lookup
  const menuItemsCache = useMemo(() => {
    if (!menu) {
      console.log('⚠️ [MENU_CHAT_BAR] No menu provided, menuItemsCache will be empty');
      return new Map();
    }
    const cache = new Map();
    menu.menuPages.forEach((page: MenuPage) => {
      page.menuItems.forEach((item: MenuItem) => {
        cache.set(item.id, item);
      });
    });
    console.log('✅ [MENU_CHAT_BAR] menuItemsCache populated with', cache.size, 'items');
    return cache;
  }, [menu]);

  // Create a wrapper for getDisplayText if not provided
  // This ensures we always have a function to use
  const displayTextFn = useMemo(() => {
    if (getDisplayText) {
      return getDisplayText;
    }
    // Fallback function if getDisplayText is not provided
    return (item: MenuItem, field: 'name' | 'description' | 'disclaimer') => {
      // Try new translations structure first
      const newTranslation = item.props?.translations?.[currentLanguage]?.[field]?.text;
      if (newTranslation) return newTranslation;

      // Legacy fallback (only friendly translation)
      if (currentLanguage === 'en') {
        if (field === 'name') return item.props?.translations?.en?.name?.text ?? item.props?.enLocalization?.name ?? item.name;
        if (field === 'description') return item.props?.translations?.en?.description?.text ?? item.props?.enLocalization?.description ?? item.description;
      }

      if (field === 'name' || field === 'description') return item[field] || '';
      return '';
    };
  }, [getDisplayText, currentLanguage]);

  // Cache extracted menu items from messages
  const messageMenuItemsCache = useMemo(() => {
    const cache = new Map();
    messages
      .filter(msg => msg.sender === 'ai')
      .forEach(msg => {
        const items = extractMenuItemsFromMessage(msg.text);
        items.forEach(item => {
          if (item) {
            cache.set(item.id, item);
          }
        });
      });
    return cache;
  }, [messages]);

  const handleSend = useCallback(() => {
    handleSendMessage();
    setShowMiniOverlay(true);
  }, [handleSendMessage]);

  const toggleChat = useCallback(() => {
    setIsChatOpen(prev => !prev);
    setShowMiniOverlay(false);
  }, []);

  /**
   * Handles clicks on menu item links in chat messages
   * Optimized to use cached data instead of expensive operations
   */
  const handleMenuItemClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (anchor && anchor.dataset.menuItem === 'true') {
      e.preventDefault();
      const itemId = anchor.dataset.itemId;
      if (itemId) {
        // Use cached menu item data for faster lookup
        const menuItem = messageMenuItemsCache.get(itemId);
        if (menuItem) {
          const fullMenuItem = menuItemsCache.get(itemId);
          if (fullMenuItem) {
            if (onMenuItemClick) {
              onMenuItemClick(itemId);
            } else if (cartEnabled) {
              // Fallback to adding to cart only when cart is enabled for this restaurant
              addItem(fullMenuItem);
              showAddedToCartNotification(fullMenuItem.props?.translations?.en?.name?.text ?? fullMenuItem.props?.enLocalization?.name ?? fullMenuItem.name);
            }

            if (scrollToMenuItem) {
              scrollToMenuItem(itemId);
            }
          } else {
            if (cartEnabled) {
              showAddedToCartNotification(`Error: Menu item "${menuItem.name}" not found`, 'error');
            }
          }
        }
      }
    }
  }, [messageMenuItemsCache, menuItemsCache, addItem, scrollToMenuItem, onMenuItemClick, cartEnabled]);

  // Ref to access the textarea for focusing
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleQuestionClick = useCallback((question: string) => {
    if (!isChatOpen) {
      setIsChatOpen(true);
      setShowMiniOverlay(false);
    }
    setNewMessage(question);
    // Send after state update propagates
    setTimeout(() => {
      handleSendMessage();
      setShowMiniOverlay(true);
    }, 50);
    onQuestionClick?.(question);
  }, [setNewMessage, isChatOpen, handleSendMessage, onQuestionClick]);

  const shouldShowMiniOverlay = showMiniOverlay && !isChatOpen && !!lastBotMessage;

  // Conditional styling for cart bar when chat is collapsed
  // Adds top border matching the chat window style (no drop shadow)
  const cartBarStyle = useMemo(() => {
    const baseStyle = styles.cartBarContainer;
    
    // Only add border when chat is collapsed
    if (!isChatOpen) {
      return {
        ...baseStyle,
        // Top border: same color as chat window, using theme palette
        borderTop: `1px solid ${isDarkMode ? theme.colors.dark[3] : theme.colors.dark[9]}`,
      };
    }
    
    return baseStyle;
  }, [styles.cartBarContainer, isChatOpen, isDarkMode, theme.colors.dark]);

  return (
    <>
      <PerformanceMonitor componentName="MenuChatBar" props={{ newMessage, isSending, messages: messages.length }} />
      <style>{animationStyles}</style>

      {/* Mini overlay - positioned absolutely when chat is closed */}
      <MiniOverlay
        lastBotMessage={lastBotMessage}
        toggleChat={toggleChat}
        show={shouldShowMiniOverlay}
        styles={styles}
        theme={theme}
        currentLanguage={currentLanguage}
      />

      {/* Main flex container for bottom components - stacks vertically without gaps */}
      <Box style={styles.bottomContainer}>
        {/* Chat Area - appears above cart and input when open */}
        {isChatOpen && (
          <ChatArea
            isOpen={isChatOpen}
            messages={messages}
            isSending={isSending}
            onClose={toggleChat}
            onMenuItemClick={handleMenuItemClick}
            onQuestionClick={handleQuestionClick}
            menuItemsMap={menuItemsCache}
            getDisplayText={displayTextFn}
            onItemSelect={onMenuItemClick}
            styles={styles}
            currentLanguage={currentLanguage}
          />
        )}

        {/* Cart Bar - visible only when cart is enabled for this restaurant */}
        {cartEnabled && (
          <Box style={cartBarStyle}>
            <CartButton
              itemCount={getTotalItems()}
              onClick={onCartButtonClick || (() => {})}
              currentLanguage={currentLanguage}
            />
          </Box>
        )}

        {/* Input Bar - always at the bottom */}
        <InputBar
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSend={handleSend}
          isSending={isSending}
          isChatOpen={isChatOpen}
          toggleChat={toggleChat}
          textareaRef={textareaRef as React.RefObject<HTMLTextAreaElement>}
          styles={styles}
          currentLanguage={currentLanguage}
        />
      </Box>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent re-renders on scroll
  // Only re-render if props that actually affect the component change
  
  // Check primitive values
  if (prevProps.newMessage !== nextProps.newMessage) return false;
  if (prevProps.isSending !== nextProps.isSending) return false;
  if (prevProps.currentLanguage !== nextProps.currentLanguage) return false;
  
  // Check messages array - only re-render if length or content changes
  if (prevProps.messages.length !== nextProps.messages.length) return false;
  // Deep compare messages to see if content changed
  for (let i = 0; i < prevProps.messages.length; i++) {
    if (prevProps.messages[i].id !== nextProps.messages[i].id ||
        prevProps.messages[i].text !== nextProps.messages[i].text ||
        prevProps.messages[i].sender !== nextProps.messages[i].sender) {
      return false;
    }
  }
  
  // Check menu object - only re-render if menu ID or cartEnabled changes
  if (prevProps.menu?.id !== nextProps.menu?.id) return false;
  if (prevProps.cartEnabled !== nextProps.cartEnabled) return false;
  
  // All other props (callbacks, functions) are assumed stable via useCallback
  // If they change, we want to re-render anyway
  return true; // Props are equal, skip re-render
});

export default MenuChatBar;
